// Lib
import React, { ReactNode, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ToolbarToolProps } from '../../../config/toolDefinitions/toolbarToolTypes';
import {
    useActiveTiptapEditorCallback,
    useTiptapActiveEditorSelector,
} from '../../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';
import RichTextTool from './RichTextTool';
import {
    selectIsMarkWithinSelection,
    selectIsNodeWithinSelection,
    selectIsSelectionRangeActive,
} from './selectors/tiptapToolSelectors';
import { createElement, getNewElementId } from '../../../../../element/actions/elementActions';
import { togglePopup } from '../../../../../components/popupPanel/popupActions';
import { getCollapsedCommentPopupId } from '../../../../../../common/elements/utils/collapsedCommentPopupUtils';
import { getElementId } from '../../../../../../common/elements/utils/elementPropertyUtils';
import { ImMNCurrentUser } from '../../../../../user/currentUserSelector';
import { ElementType } from '../../../../../../common/elements/elementTypes';
import { BoardSections } from '../../../../../../common/boards/boardConstants';
import { AttachMode } from '../../../../../../common/elements/elementModelTypes';
import { getNewTransactionId } from '../../../../../utils/undoRedo/undoRedoTransactionManager';
import { Editor } from '@tiptap/react';
import { getExtendedMarksBetween } from '../../../../../../common/tiptap/utils/getExtendedMarksBetween';
import { isEmpty } from 'lodash';
import { TiptapMarkType } from '../../../../../../common/tiptap/tiptapTypes';
import { setSelectedElements } from '../../../../../element/selection/selectionActions';

type InlineCommentToolProps = ToolbarToolProps & {
    currentUser: ImMNCurrentUser;
    currentBoardId: string;
};

const InlineCommentTool = (props: InlineCommentToolProps): ReactNode => {
    const { selectedElements } = props;

    const isCodeActive = useTiptapActiveEditorSelector(selectIsMarkWithinSelection('code'));
    const isCodeBlockActive = useTiptapActiveEditorSelector(selectIsNodeWithinSelection('codeBlock'));
    const isActive = useTiptapActiveEditorSelector(selectIsMarkWithinSelection('inlineComment'));
    const hasSelectionRange = useTiptapActiveEditorSelector(selectIsSelectionRangeActive);
    const currentlyEditingId = getElementId(selectedElements?.first());

    const isDisabled = !isActive && (isCodeActive || isCodeBlockActive || !hasSelectionRange);

    const dispatch = useDispatch();

    /**
     * Opens the attached comment popup associated with this inline comment.
     */
    const openInlineCommentPopup = useCallback((commentThreadId: string) => {
        dispatch(setSelectedElements({ ids: [commentThreadId], transactionId: undefined, rangeAnchors: undefined }));
        // Opens the inline comment popup for the newly created comment thread
        dispatch(
            togglePopup({
                popupId: getCollapsedCommentPopupId(commentThreadId),
            }),
        );
    }, []);

    /**
     * Opens the attached comment for the inline comment mark found within the current selection.
     */
    const openInlineCommentPopupForSelection = useCallback((activeEditor: Editor) => {
        const originalSelection = activeEditor.state.selection;
        const { from, to } = originalSelection;
        const markType = activeEditor.state.schema.marks.inlineComment;

        // Finds all the inline comment marks within the current selection
        const allMarks = getExtendedMarksBetween(from, to, activeEditor.state.doc, [markType]);

        const selectedCommentThreadIds: string[] = allMarks.map(
            (mark) => mark.mark.type.name === TiptapMarkType.inlineComment && mark.mark.attrs.commentThreadId,
        );

        if (isEmpty(selectedCommentThreadIds)) return;

        // Open the first selected popup
        openInlineCommentPopup(selectedCommentThreadIds[0] as string);
    }, []);

    /**
     * Adds a new inline comment to the current selection in the editor.
     */
    const addInlineComment = useCallback(
        (activeEditor: Editor) => {
            const commentThreadId = dispatch(getNewElementId()) as unknown as string;
            const transactionId = getNewTransactionId();

            // Adds the inline comment mark to the current selection
            activeEditor
                .chain()
                .toggleMark('inlineComment', { commentThreadId })
                .flushPendingUpdate({ transactionId })
                .run();

            // Creates a new comment thread element to back the inline comment
            dispatch(
                createElement(
                    {
                        elementType: ElementType.COMMENT_THREAD_TYPE,
                        location: {
                            parentId: currentlyEditingId,
                            section: BoardSections.ATTACHED,
                            position: {
                                attachMode: AttachMode.inline,
                            },
                        },
                        transactionId,
                    },
                    commentThreadId,
                ),
            );

            openInlineCommentPopup(commentThreadId);
        },
        [currentlyEditingId],
    );

    /**
     * Toggles the inline comment mark on the current selection.
     * If the mark is active, it removes all inline comments within the selection.
     * If the mark is not active, it adds an inline comment across the entire selection.
     */
    const toggleMark = useActiveTiptapEditorCallback(
        (activeEditor, event: PointerEvent) => {
            if (!activeEditor) return;

            event.preventDefault();

            isActive
                ? // Remove all inline comments if there's any active in the selection
                  openInlineCommentPopupForSelection(activeEditor)
                : // Add inline comment if there isn't any active
                  addInlineComment(activeEditor);
        },
        [isActive],
    );

    return (
        <RichTextTool
            {...props}
            disabled={isDisabled}
            isActive={isActive}
            onClick={toggleMark}
            iconName="toolbar-comment"
        />
    );
};

export default InlineCommentTool;
